import { createIdentifier } from '@blocksuite/global/di';
import type { DeepPartial } from '@blocksuite/global/utils';
import type { ExtensionType } from '@blocksuite/store';
import type { Signal } from '@preact/signals-core';
import { z } from 'zod';

import { NodePropsSchema } from '../utils/index.js';

export const GeneralSettingSchema = z
  .object({
    edgelessScrollZoom: z.boolean().default(false),
    /**
     * No modo select, arrastar a partir de um ponto VAZIO move o quadro em vez de desenhar a
     * caixa de seleção múltipla — que passa a sair com `Shift` segurado.
     *
     * Vale para apontamento indireto (mouse e trackpad, inclusive o do iPad, que o navegador
     * reporta como `mouse`) e, desde o card 1864291811674032036, também para o DEDO — onde o
     * que separa mover de selecionar é o tempo: andar logo move o quadro, ficar parado um
     * instante e só então arrastar desenha a caixa. O critério inteiro mora em
     * `blocks/surface/src/tuninho/decidir-pan.ts`.
     *
     * A caneta segue desenhando a caixa direto: o operador falou de dedo, e o que não foi
     * pedido não se muda. Dois dedos continuam movendo o quadro como sempre moveram.
     *
     * ⚠️ O `.default(true)` é o que faz a função nascer ligada — `watchAll()` do lado do
     * AFFiNE resolve toda chave não persistida por `schema.parse(undefined)`, então este
     * default é ao mesmo tempo o comportamento e o estado que o Switch mostra. Consumido em
     * `blocks/surface/src/tool/default-tool.ts`.
     */
    edgelessSelectDragPan: z.boolean().default(true),
    edgelessDisableScheduleUpdate: z.boolean().default(false),
    docCanvasPreferView: z
      .enum(['affine:embed-linked-doc', 'affine:embed-synced-doc'])
      .default('affine:embed-synced-doc'),
  })
  .merge(NodePropsSchema);

export type EditorSetting = z.infer<typeof GeneralSettingSchema>;

export interface EditorSettingService {
  setting$: Signal<DeepPartial<EditorSetting>>;
  set?: (
    key: keyof EditorSetting,
    value: EditorSetting[keyof EditorSetting]
  ) => void;
}

export const EditorSettingProvider = createIdentifier<EditorSettingService>(
  'AffineEditorSettingProvider'
);

export function EditorSettingExtension(
  service: EditorSettingService
): ExtensionType {
  return {
    setup: di => {
      di.override(EditorSettingProvider, () => service);
    },
  };
}
